// Objective PACKAGE SOLVER contract (issue #4006 P4, doc v3.1 §5.5/§7 P4).
// Types only — the deterministic solver implementation lives in the API lane
// (apps/api/src/ask-helix/objective-solver/), never here.
//
// This file makes the zero-consumer objective-value-bridge contract CONSUMED,
// never forked: the solver's lever rows ARE ObjectiveBridgeLever, its withheld
// rows ARE ObjectiveBridgeWithheldLever, its overlap receipts ARE
// DeconflictionAdjustment, and its policy receipts ARE PolicyEnforcementReceipt
// (§5.5 — the solver consumes policy, it never re-derives it).
//
// Publishability is load-bearing (§5.5 companion precondition): a
// 'candidate_only' lever NEVER sums into a committed total — it appears as
// candidate upside with its unlock reason. 'additive' requires the overlap
// receipts the contract names; a missing receipt holds the lever out with a
// named reason instead of silently summing.
import type {
  DeconflictionAdjustment,
  ObjectiveBridgeAnchor,
  ObjectiveBridgeLever,
  ObjectiveBridgeLeverTier,
  ObjectiveBridgeWithheldLever,
  ObjectiveContributionContract,
  TargetMetricUnit,
} from './objective-value-bridge';
import type { PolicyEnforcementReceipt } from './neurotruth-policy-projection';
import type {
  ObjectivePackageLeverDeconflictionOutcome,
  ObjectivePackageLeverPartialReason,
  ObjectivePackageMemberDisposition,
} from './objective-package-lever-classification';
export {
  OBJECTIVE_PACKAGE_LEVER_DECONFLICTION_OUTCOMES,
  OBJECTIVE_PACKAGE_LEVER_PARTIAL_REASONS,
  OBJECTIVE_PACKAGE_MEMBER_DISPOSITIONS,
  type ObjectivePackageLeverDeconflictionOutcome,
  type ObjectivePackageLeverPartialReason,
  type ObjectivePackageMemberDisposition,
} from './objective-package-lever-classification';

export const OBJECTIVE_PACKAGE_SOLVER_VERSION =
  'objective_package_solver_v1' as const;

export type ObjectivePackageSolverVersion =
  typeof OBJECTIVE_PACKAGE_SOLVER_VERSION;

export const OBJECTIVE_PACKAGE_AUTHORITY_VERSION =
  'objective_package_authority_v1' as const;

export type ObjectivePackageAuthorityVersion =
  typeof OBJECTIVE_PACKAGE_AUTHORITY_VERSION;

export const OBJECTIVE_PACKAGE_SOLUTION_SCHEMA_VERSION =
  'objective_package_solution_v1' as const;

export type ObjectivePackageSolutionSchemaVersion =
  typeof OBJECTIVE_PACKAGE_SOLUTION_SCHEMA_VERSION;

// ---------------------------------------------------------------------------
// Solver input — consumed contracts + the facts packaging needs
// ---------------------------------------------------------------------------

export const OBJECTIVE_PACKAGE_OVERLAP_RECEIPT_STATES = [
  'receipted',
  'missing',
] as const;

export type ObjectivePackageOverlapReceiptState =
  (typeof OBJECTIVE_PACKAGE_OVERLAP_RECEIPT_STATES)[number];

export const OBJECTIVE_PACKAGE_ANCHOR_STATES = [
  'evidence_backed',
  'blocked',
] as const;

export type ObjectivePackageAnchorState =
  (typeof OBJECTIVE_PACKAGE_ANCHOR_STATES)[number];

export const OBJECTIVE_PACKAGE_MEMBER_SOURCE_POSTURES = [
  'source_receipted',
  'modeled',
  'unknown',
] as const;

export type ObjectivePackageMemberSourcePosture =
  (typeof OBJECTIVE_PACKAGE_MEMBER_SOURCE_POSTURES)[number];

// One priced lever row entering the solver. `lever` is the consumed bridge
// contract row; the companion fields carry what packaging needs and the bridge
// row does not declare: the contract publishability class and the overlap
// receipt state for additive posture.
export type ObjectivePackageSolverLeverInput = Readonly<{
  lever: ObjectiveBridgeLever;
  publishability: ObjectiveContributionContract['publishability'];
  // 'receipted' = the de-confliction receipts required for additive posture
  // exist (or are provably unnecessary: no declared-overlapping counterpart in
  // this picture). 'missing' = at least one overlap pair is unresolved; the
  // pairs are named so the hold-out reason is specific, never generic.
  overlapReceiptState: ObjectivePackageOverlapReceiptState;
  missingReceiptPairs: readonly string[];
  // What would unlock a held lever (candidate_only / receipt-missing states);
  // null when nothing is held.
  unlockReason: string | null;
  // NON-AUTHORITATIVE LEGACY METADATA (Story 3, #5043). Package membership is
  // the solver's decision alone, derived from eligibility (publishability,
  // overlap receipts, source posture, positive governed value). This field
  // survives only so bridge-era callers still typecheck; the solver never reads
  // it to select, filter, or force a member. Producers, bridge-era adapters and
  // input builders must not write it as an authority decision.
  selectedForManagementPackage?: boolean;
  // Source posture of this member's selected value. Modeled values are excluded
  // from the dependable minimum and ride only as a disclosed selected estimate.
  sourcePosture: ObjectivePackageMemberSourcePosture;
  // Required when a selected modeled member is published as an estimate.
  sourceDisclosure: string | null;
}>;

export type ObjectivePackageSolverInput = Readonly<{
  // Consumed bridge anchor: evidence-backed (baseline/target/delta) or blocked.
  anchor: ObjectiveBridgeAnchor;
  // The objective metric unit every committed value must carry. A lever row in
  // a different unit is a contract violation and fails the solve closed.
  unit: TargetMetricUnit;
  levers: readonly ObjectivePackageSolverLeverInput[];
  // Honest withholds (consumed contract rows). Policy-excluded levers ride
  // here too — the solver cross-references `policyReceipts` to classify them
  // as policy_excluded (receipt-backed) instead of evidence_withheld.
  withheldLevers: readonly ObjectiveBridgeWithheldLever[];
  // Receipts of the de-confliction already applied to lever contribution
  // values (consumed contract rows) — echoed for audit, never re-applied.
  deconflictionAdjustments: readonly DeconflictionAdjustment[];
  // §5.5: enforcement receipts from the eligibility/scenario/solving points.
  // The solver CONSUMES these — it never re-derives policy from a projection.
  policyReceipts: readonly PolicyEnforcementReceipt[];
  // Digest of the approved NeuroTruthDecisionPolicyProjection in force (null
  // when no projection exists) — joins the solution for reuse fingerprints.
  policyProjectionDigest: string | null;
}>;

// ---------------------------------------------------------------------------
// Per-lever inclusion state (closed vocabulary)
// ---------------------------------------------------------------------------

export const OBJECTIVE_PACKAGE_LEVER_INCLUSION_STATES = [
  // In the committed package: additive publishability + overlap receipts +
  // positive governed value in the objective unit.
  'included',
  // Governed value exists but the contract publishability is 'candidate_only':
  // shown as candidate upside with its unlock reason, NEVER summed.
  'candidate_only_held',
  // Additive publishability but the de-confliction receipts the contract
  // names do not exist for at least one overlap pair — held out, pairs named.
  'receipt_missing',
  // Excluded by an approved NeuroTruth policy projection at the eligibility
  // point — always backed by a PolicyEnforcementReceipt id.
  'policy_excluded',
  // Honestly withheld on evidence (or carries no publishable number).
  'evidence_withheld',
  // Not additive into this objective's committed total: 'non_additive' /
  // 'cash_only' publishability, or a non-positive governed value. Visible,
  // never summed.
  'non_additive_excluded',
] as const;

export type ObjectivePackageLeverInclusionState =
  (typeof OBJECTIVE_PACKAGE_LEVER_INCLUSION_STATES)[number];

// Every lever the solver saw — priced, held, withheld, or policy-excluded —
// appears exactly once, with a non-empty named reason. Nothing is silently
// omitted (render-open law).
export type ObjectivePackageLeverAssessment = Readonly<{
  leverFamilyId: string;
  moduleId: string | null;
  label: string;
  tier: ObjectiveBridgeLeverTier;
  inclusionState: ObjectivePackageLeverInclusionState;
  /** The solver-authored structural cause behind the inclusion state. */
  partialReason: ObjectivePackageLeverPartialReason;
  /** The solver-authored outcome for this row's overlap atoms. */
  deconflictionOutcome: ObjectivePackageLeverDeconflictionOutcome;
  // Named reason for the state; never empty, never softened downstream.
  reason: string;
  // The governed value carried by the row (candidate upside for held rows);
  // null when no publishable number exists.
  contributionValue: number | null;
  unit: TargetMetricUnit;
  numericBindingKey: string | null;
  deconflictionAdjustmentValue: number;
  // Position in the curated sequencing when included; null otherwise.
  sequenceIndex: number | null;
  unlockReason: string | null;
  // Receipt ids backing a policy_excluded state (empty otherwise).
  policyReceiptIds: readonly string[];
  evidenceRefIds: readonly string[];
  /** Set only on an INCLUDED member; null on every other inclusion state. */
  disposition: ObjectivePackageMemberDisposition | null;
}>;

// ---------------------------------------------------------------------------
// Coverage + alternatives
// ---------------------------------------------------------------------------

export type ObjectivePackageCoverage = Readonly<{
  // Sum of included levers (cent-exact). Null when nothing is committable.
  committedPackageValue: number | null;
  // max(0, targetDelta - committed), cent-exact. Null when the anchor is
  // blocked (no target to measure against).
  residualGapValue: number | null;
  // How much of the target the committed package fills, capped at 100.
  // ("targetFill", never "coverage" — the coverage vocabulary produced the
  // "100% covered" overclaim.)
  targetFillPct: number | null;
  // Null when the anchor is blocked.
  targetMet: boolean | null;
  candidateOnlyLeverCount: number;
  receiptMissingLeverCount: number;
  policyExcludedLeverCount: number;
  evidenceWithheldLeverCount: number;
}>;

// ---------------------------------------------------------------------------
// Canonical selected package authority (#4115 P1)
// ---------------------------------------------------------------------------

export const OBJECTIVE_PACKAGE_AUTHORITY_WITHHELD_REASON_CODES = [
  'anchor_blocked',
  'no_selected_members',
  'selected_member_not_additive',
  'selected_member_receipt_missing',
  'selected_member_source_unknown',
  'selected_modeled_disclosure_missing',
] as const;

export type ObjectivePackageAuthorityWithheldReasonCode =
  (typeof OBJECTIVE_PACKAGE_AUTHORITY_WITHHELD_REASON_CODES)[number];

export const OBJECTIVE_PACKAGE_AUTHORITY_RESIDUAL_KINDS = [
  'measured',
  'withheld',
] as const;

export type ObjectivePackageAuthorityResidualKind =
  (typeof OBJECTIVE_PACKAGE_AUTHORITY_RESIDUAL_KINDS)[number];

export type ObjectivePackageAuthorityWithheldReason = Readonly<{
  reasonCode: ObjectivePackageAuthorityWithheldReasonCode;
  reasonPlain: string;
  memberIds: readonly string[];
}>;

export type ObjectivePackageAuthorityReceiptBinding = Readonly<{
  memberId: string;
  sourcePosture: Exclude<ObjectivePackageMemberSourcePosture, 'unknown'>;
  contributionClaimIds: readonly string[];
  deconflictionAdjustmentClaimIds: readonly string[];
  evidenceRefIds: readonly string[];
}>;

export type ObjectivePackageAuthorityAmountSlot = Readonly<{
  amountValue: number | null;
  memberIds: readonly string[];
  receiptIds: readonly string[];
  evidenceRefIds: readonly string[];
  memberReceiptBindings: readonly ObjectivePackageAuthorityReceiptBinding[];
  withheldReason: ObjectivePackageAuthorityWithheldReason | null;
}>;

export type ObjectivePackageSelectedEstimatedAddition = Readonly<{
  amountValue: number | null;
  memberIds: readonly string[];
  receiptIds: readonly string[];
  evidenceRefIds: readonly string[];
  memberReceiptBindings: readonly ObjectivePackageAuthorityReceiptBinding[];
  disclosure: string | null;
  withheldReason: ObjectivePackageAuthorityWithheldReason | null;
}>;

export type ObjectivePackageResidualAgainstSelectedManagementPackage =
  | Readonly<{
      kind: 'measured';
      basis: 'selected_management_package';
      residualGapValue: number;
      targetFillPct: number;
      targetMet: boolean;
    }>
  | Readonly<{
      kind: 'withheld';
      reason: ObjectivePackageAuthorityWithheldReason;
    }>;

export type ObjectivePackageAuthority = Readonly<{
  authorityVersion: ObjectivePackageAuthorityVersion;
  packageId: 'selected_management_package';
  packageVersion: ObjectivePackageSolutionSchemaVersion;
  asOf: string | null;
  provenance: Readonly<{
    producerId: ObjectivePackageSolverVersion;
    sourceFactPath: 'objectivePackageSolver.packageAuthority';
    policyProjectionDigest: string | null;
    anchorEvidenceRefIds: readonly string[];
  }>;
  // Only selected, additive, de-conflicted, source-receipted members.
  dependableMinimum: ObjectivePackageAuthorityAmountSlot;
  // Nullable selected modeled estimate, always disclosed when present.
  selectedEstimatedAddition: ObjectivePackageSelectedEstimatedAddition;
  // Exact additive union of dependableMinimum and selectedEstimatedAddition.
  selectedManagementPackage: ObjectivePackageAuthorityAmountSlot;
  // Exactly one measured residual/target fill against selectedManagementPackage,
  // or one typed reason stating why the measurement is withheld.
  residualAgainstSelectedManagementPackage: ObjectivePackageResidualAgainstSelectedManagementPackage;
}>;

export const OBJECTIVE_PACKAGE_ALTERNATIVE_IDS = [
  // The combination that closes the gap with the least surplus (fewest levers
  // on ties). Present only when some committed subset meets the target.
  'best_target_fit',
  // The fewest-lever combination that still closes the gap.
  'smallest_set',
] as const;

export type ObjectivePackageAlternativeId =
  (typeof OBJECTIVE_PACKAGE_ALTERNATIVE_IDS)[number];

export type ObjectivePackageAlternative = Readonly<{
  alternativeId: ObjectivePackageAlternativeId;
  label: string;
  // Subset of the committed package, in curated sequence order.
  leverFamilyIds: readonly string[];
  packageValue: number;
  residualGapValue: number;
  targetFillPct: number;
  rationale: string;
}>;

// ---------------------------------------------------------------------------
// Public policy-exclusion projection (closed V1 UI contract)
// ---------------------------------------------------------------------------

export type PolicyExclusionCardPropsV1 = Readonly<{
  title: string;
  summary: string;
  exclusions: readonly Readonly<{
    label: string;
    reason: string;
    basis: string;
  }>[];
}>;

export type ClaimOmissionCardPropsV1 = Readonly<{
  title: string;
  summary: string;
  unlock: string;
}>;

export const RECOMMENDED_PACKAGE_SECTION_SCHEMA_VERSION =
  'recommended_package_section_v1' as const;

export type RecommendedPackageEstimatedAdditionDisclosure =
  'modeled_estimate_included';

export type RecommendedPackageLeverValueBasis =
  | 'source_backed'
  | 'modeled_estimate';

export type RecommendedPackageSectionPropsV1 = Readonly<{
  schemaVersion: typeof RECOMMENDED_PACKAGE_SECTION_SCHEMA_VERSION;
  availability: 'available' | 'unavailable';
  objective: Readonly<{
    metricLabel: string;
    targetDeltaValue: `{{n:${string}}}` | null;
    unit: TargetMetricUnit;
  }>;
  recommendedPackage: Readonly<{
    packageValue: `{{n:${string}}}` | null;
    residualGapValue: `{{n:${string}}}` | null;
    targetFillPct: `{{n:${string}}}` | null;
    targetMet: boolean | null;
    estimatedAdditionDisclosure: RecommendedPackageEstimatedAdditionDisclosure | null;
  }>;
  levers: readonly Readonly<{
    label: string;
    tier: 'approved' | 'advisory' | 'unlockable';
    status: 'included' | 'candidate_held' | 'overlap_review_required' | 'held_by_policy' | 'evidence_required' | 'not_additive';
    statusLabel: string;
    reason: string;
    contributionValue: `{{n:${string}}}` | null;
    unit: TargetMetricUnit;
    unlockReason: string | null;
    valueBasis: RecommendedPackageLeverValueBasis;
  }>[];
  alternatives: readonly Readonly<{
    label: string;
    includedLeverLabels: readonly string[];
    packageValue: null;
    residualGapValue: null;
    targetFillPct: null;
    rationale: string;
  }>[];
  gaps: readonly Readonly<{
    title: string;
    detail: string;
    supplyHint: string;
  }>[];
}>;

function isRecommendedPackageRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasRecommendedPackageKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  return actual.length === expected.length && actual.every((key, index) => key === expected[index]);
}

/**
 * The sole public explanation allowed when a closed public component is moved
 * to workings. Keeping this as an exact shared contract prevents runtime and
 * renderer vocabulary from becoming user-facing copy.
 */
export const PUBLIC_CLOSED_COMPONENT_WORKINGS_REASON =
  'Supporting detail is kept here so the decision remains clear.' as const;

export type PublicClosedComponentWorkingsReason =
  typeof PUBLIC_CLOSED_COMPONENT_WORKINGS_REASON;

/**
 * The flagship renderer may add this bounded workings stamp after a public
 * component is projected. The shared public contract owns the only exception
 * to the otherwise exact top-level key set; nested public objects remain exact.
 */
function hasExactPublicComponentKeys(
  value: Record<string, unknown>,
  baseKeys: readonly string[],
): boolean {
  if (hasRecommendedPackageKeys(value, baseKeys)) return true;
  return (
    hasRecommendedPackageKeys(value, [...baseKeys, 'renderMode', 'workingsReason']) &&
    value.renderMode === 'workings' &&
    isPublicWorkingsReason(value.workingsReason)
  );
}

/**
 * RecommendedPackageSection is the only closed package projection that the
 * flagship renderer elevates into its own executive section. Keep its exact
 * render stamp local so qualitative cards cannot inherit that admission.
 */
function hasExactRecommendedPackageSectionKeys(
  value: Record<string, unknown>,
  baseKeys: readonly string[],
): boolean {
  if (hasExactPublicComponentKeys(value, baseKeys)) return true;
  return (
    hasRecommendedPackageKeys(value, [
      ...baseKeys,
      'renderMode',
      'renderRole',
      'renderOrder',
      'renderOrderWithinSection',
    ]) &&
    value.renderMode === 'executive' &&
    value.renderRole === 'recommended_package' &&
    value.renderOrder === 4 &&
    value.renderOrderWithinSection === 0
  );
}

function isRecommendedPackagePublicText(value: unknown): value is string {
  return typeof value === 'string' && value.trim() === value && value.length > 0 &&
    !/\d|_|\{\{|\}\}|\b(?:id|ids|receipt|receipts|solver|authority|numeric|proof|runtime|schema|internal)\b|(?:objectivepackage|packageauthority|leverfamily)/iu.test(value);
}

function isRecommendedPackageToken(value: unknown): value is `{{n:${string}}}` {
  return typeof value === 'string' && /^\{\{n:[a-z0-9_:.-]+\}\}$/iu.test(value);
}

/** Closed public renderer contract; internal solver and receipt fields never cross it. */
export function isRecommendedPackageSectionPropsV1(value: unknown): value is RecommendedPackageSectionPropsV1 {
  if (!isRecommendedPackageRecord(value) || !hasExactRecommendedPackageSectionKeys(value, ['schemaVersion', 'availability', 'objective', 'recommendedPackage', 'levers', 'alternatives', 'gaps']) ||
    value.schemaVersion !== RECOMMENDED_PACKAGE_SECTION_SCHEMA_VERSION || !['available', 'unavailable'].includes(String(value.availability)) ||
    !isRecommendedPackageRecord(value.objective) || !hasRecommendedPackageKeys(value.objective, ['metricLabel', 'targetDeltaValue', 'unit']) ||
    !isRecommendedPackagePublicText(value.objective.metricLabel) || (value.availability === 'available' ? !isRecommendedPackageToken(value.objective.targetDeltaValue) : value.objective.targetDeltaValue !== null) || !isRecommendedPackagePublicText(value.objective.unit) ||
    !isRecommendedPackageRecord(value.recommendedPackage) || !hasRecommendedPackageKeys(value.recommendedPackage, ['packageValue', 'residualGapValue', 'targetFillPct', 'targetMet', 'estimatedAdditionDisclosure']) ||
    ![null, 'modeled_estimate_included'].includes(value.recommendedPackage.estimatedAdditionDisclosure as null | string) ||
    (value.availability === 'available'
      ? (!isRecommendedPackageToken(value.recommendedPackage.packageValue) || !isRecommendedPackageToken(value.recommendedPackage.residualGapValue) || !isRecommendedPackageToken(value.recommendedPackage.targetFillPct) || typeof value.recommendedPackage.targetMet !== 'boolean')
      : (value.recommendedPackage.packageValue !== null || value.recommendedPackage.residualGapValue !== null || value.recommendedPackage.targetFillPct !== null || value.recommendedPackage.targetMet !== null)) ||
    !Array.isArray(value.levers) || !Array.isArray(value.alternatives) || !Array.isArray(value.gaps)) return false;
  const recommendedPackage = isRecommendedPackageRecord(value.recommendedPackage)
    ? value.recommendedPackage
    : null;
  const objective = isRecommendedPackageRecord(value.objective)
    ? value.objective
    : null;
  if (!recommendedPackage || !objective) return false;
  const statuses = {
    included: ['Included', 'token', 'null'],
    candidate_held: ['Candidate held', 'null', 'text'],
    overlap_review_required: ['Overlap review required', 'null', 'text'],
    held_by_policy: ['Held by policy', 'null', 'text'],
    evidence_required: ['Evidence required', 'null', 'text'],
    not_additive: ['Not additive', 'null', 'null'],
  } as const;
  const includedLabels = new Set<string>();
  let hasModeledEstimate = false;
  const leversValid = value.levers.every((lever) => {
    if (!isRecommendedPackageRecord(lever) || !hasRecommendedPackageKeys(lever, ['label', 'tier', 'status', 'statusLabel', 'reason', 'contributionValue', 'unit', 'unlockReason', 'valueBasis']) ||
      !isRecommendedPackagePublicText(lever.label) || !['approved', 'advisory', 'unlockable'].includes(String(lever.tier)) ||
      !isRecommendedPackagePublicText(lever.reason) || lever.unit !== objective.unit ||
      !['source_backed', 'modeled_estimate'].includes(String(lever.valueBasis))) return false;
    const status = statuses[lever.status as keyof typeof statuses];
    if (!status || lever.statusLabel !== status[0] ||
      (value.availability === 'available' && status[1] === 'token' && !isRecommendedPackageToken(lever.contributionValue)) ||
      (value.availability === 'unavailable' && lever.contributionValue !== null) ||
      (status[1] === 'null' && lever.contributionValue !== null) ||
      (status[2] === 'null' && lever.unlockReason !== null) ||
      (status[2] === 'text' && !isRecommendedPackagePublicText(lever.unlockReason))) return false;
    if (lever.status === 'included') includedLabels.add(lever.label);
    if (lever.valueBasis === 'modeled_estimate') hasModeledEstimate = true;
    return true;
  });
  return leversValid &&
    recommendedPackage.estimatedAdditionDisclosure ===
      (hasModeledEstimate ? 'modeled_estimate_included' : null) &&
    ['USD', 'PCT', 'BPS', 'COUNT'].includes(String(value.objective.unit)) &&
    (value.availability === 'available' ? includedLabels.size > 0 : value.alternatives.length === 0 && value.gaps.length > 0) && value.alternatives.every((alternative) =>
    isRecommendedPackageRecord(alternative) && hasRecommendedPackageKeys(alternative, ['label', 'includedLeverLabels', 'packageValue', 'residualGapValue', 'targetFillPct', 'rationale']) &&
    isRecommendedPackagePublicText(alternative.label) && Array.isArray(alternative.includedLeverLabels) && alternative.includedLeverLabels.length > 0 && alternative.includedLeverLabels.every((label) => isRecommendedPackagePublicText(label) && includedLabels.has(label)) &&
    alternative.packageValue === null && alternative.residualGapValue === null && alternative.targetFillPct === null && isRecommendedPackagePublicText(alternative.rationale) && recommendedPackage.targetMet === true
  ) && value.gaps.every((gap) =>
    isRecommendedPackageRecord(gap) && hasRecommendedPackageKeys(gap, ['title', 'detail', 'supplyHint']) &&
    isRecommendedPackagePublicText(gap.title) && isRecommendedPackagePublicText(gap.detail) && isRecommendedPackagePublicText(gap.supplyHint)
  );
}

const POLICY_EXCLUSION_NON_PUBLIC_TEXT_TERMS = new Set([
  'adapter',
  'backend',
  'binding',
  'catalog',
  'config',
  'digest',
  'dto',
  'endpoint',
  'frontend',
  'id',
  'ids',
  'internal',
  'manifest',
  'metadata',
  'payload',
  'projection',
  'receipt',
  'ref',
  'refs',
  'runtime',
  'schema',
  'tenant',
  'validator',
]);

const POLICY_EXCLUSION_NON_PUBLIC_TEXT_PAIRS = new Set([
  'allowed action',
  'allowed actions',
  'brief spec',
  'brief specs',
  'effect kind',
  'effect kinds',
  'evidence ref',
  'evidence refs',
  'lever families',
  'lever family',
  'numeric claim',
  'numeric claims',
  'proof ref',
  'proof refs',
  'run id',
  'run ids',
  'source key',
  'source keys',
]);

const POLICY_EXCLUSION_NON_PUBLIC_COMPOUND_STEMS = [
  'allowedaction',
  'briefspec',
  'effectkind',
  'evidenceref',
  'internalvalue',
  'numericclaim',
  'numericpropevidence',
  'payload',
  'policyreceiptid',
  'projectiondigest',
  'proofref',
  'receiptid',
  'runid',
  'runtime',
  'schemaversion',
  'sourcekey',
];

function isPolicyExclusionRawFieldForm(value: string): boolean {
  const trimmed = value.trim();
  return (
    /^(?:source|effect)$/iu.test(trimmed) ||
    /\b(?:source|effect)\s*:(?=\s*\S)/iu.test(value) ||
    /\b(?:source|effect)-[\p{L}][\p{L}-]*\b/iu.test(value) ||
    /\b(?:sourceKey|effectKind)\b/iu.test(value)
  );
}

function isPolicyExclusionPublicTextToken(value: string): boolean {
  if (isPolicyExclusionRawFieldForm(value)) return false;
  const words = value.match(/\p{L}+/gu) ?? [];
  if (words.length === 0) return false;

  const segments = words
    .flatMap(
      (word) =>
        word.match(/[A-Z]+(?=[A-Z][a-z]|$)|[A-Z]?[a-z]+/g) ?? [word],
    )
    .map((segment) => segment.toLowerCase());
  if (
    segments.some((segment) =>
      POLICY_EXCLUSION_NON_PUBLIC_TEXT_TERMS.has(segment),
    )
  ) {
    return false;
  }
  if (
    segments.some((segment, index) =>
      POLICY_EXCLUSION_NON_PUBLIC_TEXT_PAIRS.has(
        `${segment} ${segments[index + 1] ?? ''}`,
      ),
    )
  ) {
    return false;
  }
  return !words.some((word) =>
    POLICY_EXCLUSION_NON_PUBLIC_COMPOUND_STEMS.some((fragment) =>
      word.toLowerCase().includes(fragment),
    ),
  );
}

function isPolicyExclusionRecord(
  value: unknown,
): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasPolicyExclusionExactKeys(
  value: Record<string, unknown>,
  expectedKeys: readonly string[],
): boolean {
  const actualKeys = Object.keys(value).sort();
  const expected = [...expectedKeys].sort();
  return (
    actualKeys.length === expected.length &&
    actualKeys.every((key, index) => key === expected[index])
  );
}

function isPolicyExclusionPublicText(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length > 0 &&
    value.trim() === value &&
    !/\d|\{\{|\}\}|[a-z]+_[a-z_]*/i.test(value) &&
    isPolicyExclusionPublicTextToken(value)
  );
}

function isPublicWorkingsReason(
  value: unknown,
): value is PublicClosedComponentWorkingsReason {
  return value === PUBLIC_CLOSED_COMPONENT_WORKINGS_REASON;
}

function isClaimOmissionPublicText(value: unknown): value is string {
  return (
    isPolicyExclusionPublicText(value) &&
    !/(?:absent\s*reason|absentreason|contribution\s*claim|winner\s*headline|winnerheadline)/iu.test(
      value,
    )
  );
}

export function isClaimOmissionCardPropsV1(
  value: unknown,
): value is ClaimOmissionCardPropsV1 {
  return (
    isPolicyExclusionRecord(value) &&
    hasExactPublicComponentKeys(value, ['title', 'summary', 'unlock']) &&
    isClaimOmissionPublicText(value.title) &&
    isClaimOmissionPublicText(value.summary) &&
    isClaimOmissionPublicText(value.unlock)
  );
}

/**
 * Runtime admission predicate for the public PolicyExclusionCard V1 contract.
 * The card is qualitative only: it never carries numeric values, binding
 * tokens, receipt identifiers, or projection internals.
 */
export function isPolicyExclusionCardPropsV1(
  value: unknown,
): value is PolicyExclusionCardPropsV1 {
  if (
    !isPolicyExclusionRecord(value) ||
    !hasExactPublicComponentKeys(value, ['title', 'summary', 'exclusions']) ||
    !isPolicyExclusionPublicText(value.title) ||
    !isPolicyExclusionPublicText(value.summary) ||
    !Array.isArray(value.exclusions) ||
    value.exclusions.length === 0
  ) {
    return false;
  }

  return value.exclusions.every(
    (exclusion) =>
      isPolicyExclusionRecord(exclusion) &&
      hasPolicyExclusionExactKeys(exclusion, ['label', 'reason', 'basis']) &&
      isPolicyExclusionPublicText(exclusion.label) &&
      isPolicyExclusionPublicText(exclusion.reason) &&
      isPolicyExclusionPublicText(exclusion.basis),
  );
}

// ---------------------------------------------------------------------------
// Solution
// ---------------------------------------------------------------------------

export type ObjectivePackageSolution = Readonly<{
  schemaVersion: ObjectivePackageSolutionSchemaVersion;
  solverVersion: ObjectivePackageSolverVersion;
  generatedAt: string;
  anchorState: ObjectivePackageAnchorState;
  unit: TargetMetricUnit;
  // The ask (anchor targetDelta); null when the anchor is blocked.
  targetDeltaValue: number | null;
  coverage: ObjectivePackageCoverage;
  packageAuthority: ObjectivePackageAuthority;
  // Curated, stable sequencing of the committed package: contribution value
  // descending, lever family id ascending on ties. Deterministic by law.
  sequencedLeverFamilyIds: readonly string[];
  levers: readonly ObjectivePackageLeverAssessment[];
  alternatives: readonly ObjectivePackageAlternative[];
  policy: Readonly<{
    projectionDigest: string | null;
    receiptIds: readonly string[];
  }>;
  // Echoed de-confliction receipts (consumed contract rows) for audit.
  deconflictionAdjustments: readonly DeconflictionAdjustment[];
}>;
